/**
 * Storage Interfaces
 * 
 * Defines the data structures for persistence.
 * Note: User content is NEVER persisted - only configurations.
 * 
 * @see docs/Research/DesignSpecsClaude.md Section 6.3
 */

import type { IPipelineStep } from './IPipeline';

/**
 * A saved pipeline configuration.
 * Note: Only saves structure, never the actual data that was processed.
 */
export interface ISavedPipeline {
  /** Unique identifier */
  id: string;
  /** User-provided name */
  name: string;
  /** Optional description */
  description?: string;
  /** Semantic type of the input the pipeline expects (by semanticType name). */
  inputSemanticType?: string;
  /** Semantic type of the output the pipeline produces (by semanticType name). */
  outputSemanticType?: string;
  /** Pipeline steps (without runtime data) */
  steps: Omit<IPipelineStep, 'id'>[];
  /** Optional pipeline-scoped variables available as {{var}} during execution */
  variables?: Record<string, string>;
  /** Creation timestamp */
  createdAt: Date;
  /** Last update timestamp */
  updatedAt: Date;
  /** Optional tags for organization */
  tags?: string[];
  /** Optional saved input data for testing/examples */
  inputData?: string;
  /** Optional input type name (semantic type) */
  inputType?: string;
}

/**
 * A user-defined preset for a tool (config snapshot + custom name).
 * Acts like an "add-on" wrapper around an existing tool.
 */
export interface IToolPreset {
  /** Unique id for the preset (stored and referenced by favorites, etc.) */
  id: string;
  /** Base tool id this preset wraps */
  toolId: string;
  /** User-facing preset name */
  name: string;
  /** Snapshot of tool config */
  config: Record<string, unknown>;
  /** ISO timestamp */
  createdAt: string;
  /** ISO timestamp */
  updatedAt: string;
}

/**
 * User preferences that persist across sessions.
 */
export interface IUserPreferences {
  /** UI theme */
  theme: 'light' | 'dark' | 'system';
  /** Editor font size */
  fontSize: 'small' | 'medium' | 'large';
  /** Auto-run pipeline on input change */
  autoRunPipeline: boolean;
  /** Auto-copy the latest output to clipboard after a successful run */
  autoCopyOutputToClipboard: boolean;
  /** Show intermediate results in pipeline view */
  showIntermediateResults: boolean;
  /** Show bytes/chars/lines footer under input/output */
  showIoStats: boolean;
  /** When a step has a custom label, optionally also show the underlying tool name */
  showToolNameWithStepLabel: boolean;
  /** Default indent size for JSON formatting */
  defaultIndentSize: number;
  /** Enable local usage statistics */
  enableLocalTelemetry: boolean;
  /** Automatically show visualizers based on detected content type */
  autoVisualizers: boolean;
  /** Show visualizer steps in the pipeline view */
  showVisualizersInPipeline: boolean;
  /** Sidebar collapsed state */
  sidebarCollapsed: boolean;
  /** Last opened pipeline ID */
  lastOpenedPipelineId?: string;
  /** Opt-in: persist input/output history across sessions */
  persistHistory: boolean;
}

/**
 * Default preferences for new users.
 */
export const DEFAULT_PREFERENCES: IUserPreferences = {
  theme: 'system',
  fontSize: 'medium',
  autoRunPipeline: true,
  autoCopyOutputToClipboard: false,
  showIntermediateResults: true,
  showIoStats: false,
  showToolNameWithStepLabel: false,
  defaultIndentSize: 2,
  enableLocalTelemetry: false,
  autoVisualizers: false,
  showVisualizersInPipeline: false,
  sidebarCollapsed: false,
  persistHistory: false,
};

/**
 * Local telemetry data (stored locally only, never sent anywhere).
 */
export interface ILocalTelemetry {
  /** Tool usage statistics */
  toolUsage: Record<string, {
    count: number;
    lastUsed: Date;
    avgDuration: number;
  }>;
  /** Common pipeline patterns */
  pipelinePatterns: Array<{
    pattern: string[];
    count: number;
  }>;
  /** Input type frequency */
  inputTypeHistory: Record<string, number>;
}

/**
 * History entry for recent operations.
 */
export interface IHistoryEntry {
  /** Unique identifier */
  id: string;
  /** Timestamp */
  timestamp: Date;
  /** Tool or pipeline used */
  toolId?: string;
  pipelineId?: string;
  /** Brief description of operation */
  description: string;
}

